import {
  init,
  backButton,
  settingsButton,
  swipeBehavior,
  closingBehavior,
  viewport,
  hapticFeedback,
  miniApp,
} from "@telegram-apps/sdk-react";
import WebApp from "@twa-dev/sdk";

import { logMsg, logEx, crumb } from "@/config/sentry-logger";

type TgInitOptions = {
  patchTonConnectOpen?: boolean;
};

const ensureTelegramGlobal = () => {
  try {
    const w = window as any;
    w.Telegram = w.Telegram || {};
    if (!w.Telegram.WebApp) {
      w.Telegram.WebApp = WebApp;
    }
    try {
      w.Telegram.WebApp.ready();
      w.Telegram.WebApp.expand();
    } catch (_) {
      /* no-op */
    }
    crumb("tg:ensureTelegramGlobal done");
  } catch (e) {
    logMsg("tg:ensureTelegramGlobal failed", "warning");
    logEx(e, "ensureTelegramGlobal");
  }
};

const patchWindowOpenForTonConnect = () => {
  try {
    const origOpen = window.open ? window.open.bind(window) : undefined;

    if ((window as any).__tcOpenPatched) return;
    (window as any).__tcOpenPatched = true;

    // @ts-ignore
    window.open = (u: any, ...args: any[]) => {
      const urlStr = typeof u === "string" ? u : String(u);
      let url: URL | null = null;
      try {
        url = new URL(urlStr);
      } catch {}

      const wa: any = (window as any).Telegram?.WebApp;
      const isTgAndroid =
        !!wa && wa.platform === "android" && typeof wa.openLink === "function";

      const walletHosts: Set<string> =
        (window as any).__tcWalletHosts || new Set();
      const isHttps = !!url && url.protocol === "https:";
      const isWalletHost = !!url && walletHosts.has(url.hostname);

      if (isTgAndroid && isHttps && isWalletHost) {
        try {
          // eslint-disable-next-line no-console
          console.log("[TC openLink] url:", urlStr);
          wa.openLink(urlStr);

          return null;
        } catch {}
      }

      return origOpen ? origOpen(u, ...args) : null;
    };

    crumb("tg:patchWindowOpenForTonConnect applied");
  } catch (e) {
    logMsg("tg:patchWindowOpenForTonConnect failed", "warning");
    logEx(e, "patchWindowOpenForTonConnect");
  }
};

export const initializeTelegramWebApp = async (opts?: TgInitOptions) => {
  crumb("tg:init start");

  ensureTelegramGlobal();

  try {
    init();
    crumb("tg:init called");
  } catch (e) {
    logMsg("tg:init failed", "warning");
    logEx(e, "tg init");
  }

  try {
    backButton.mount.ifAvailable();
    settingsButton.mount.ifAvailable();
    swipeBehavior.mount.ifAvailable();
    closingBehavior.mount.ifAvailable();
    viewport.mount.ifAvailable();
    crumb("tg:components mounted");
  } catch (e) {
    logMsg("tg:mount components failed", "warning");
    logEx(e, "tg components mount");
  }

  try {
    backButton.hide.ifAvailable();
    closingBehavior.enableConfirmation.ifAvailable();
    swipeBehavior.disableVertical.ifAvailable();
    hapticFeedback.impactOccurred.ifAvailable("heavy");
    viewport.expand.ifAvailable();
    crumb("tg:components configured");
  } catch (e) {
    logMsg("tg:configure components failed", "warning");
    logEx(e, "tg components configure");
  }

  try {
    if (miniApp.isSupported()) {
      await miniApp.mount();
      crumb("tg:miniApp mounted");

      try {
        miniApp.setHeaderColor.ifAvailable("#000000");
        miniApp.setBackgroundColor.ifAvailable("#000000");
        miniApp.setBottomBarColor.ifAvailable("#000000");
        crumb("tg:miniApp colors set", {
          header: "#000000",
          bg: "#000000",
          bottomBar: "#000000",
        });
      } catch (e) {
        logMsg("tg:miniApp set colors failed", "warning");
        logEx(e, "miniApp set colors");
      }

      try {
        miniApp.ready.ifAvailable();
        logMsg("tg:miniApp ready", "info");
      } catch (e) {
        logMsg("tg:miniApp ready failed", "warning");
        logEx(e, "miniApp.ready");
      }
    } else {
      logMsg("tg:miniApp not supported", "debug");
    }
  } catch (e) {
    logMsg("tg:miniApp mount failed", "warning");
    logEx(e, "miniApp.mount");
  }

  if (opts?.patchTonConnectOpen) {
    patchWindowOpenForTonConnect();
  }
};
